import React, { useEffect, useState } from 'react';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Rating,
  SvgIcon,
} from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { Stack } from '@mui/system';

import { UserPicture } from './UserPicture';
import Spin from 'components/Spin';
import Text from 'components/Text';
import { useUser } from 'global/UserContext';
import { useToast } from 'hooks/useToast';
import GeneralConfirmationModal from 'modals/GeneralConfirmationModal';
import { apiNetworking } from 'services/apiService';

interface MappedMember {
  label: string;
  id: string;
}

interface ValueLabelField {
  value: string;
  label: string;
}

interface EdgeTypeExtras {
  values: ValueLabelField[];
}

interface EdgeType {
  code: string;
  extras: EdgeTypeExtras | null;
  label: string;
  weight: number;
}

interface InputComponentProps {
  selectedUser: MappedMember;
}

interface CommonEdge {
  type_code: string;
}

const connectionEdgesTypes = ['CONNECTION', 'CONTACT', 'INFLUENCER'];

export const InputComponent: React.FC<InputComponentProps> = ({ selectedUser }) => {
  const { user } = useUser();
  const { toastSuccess, toastError } = useToast();

  const [loading, setLoading] = useState<boolean>(true);

  const [selectedEdgeType, setSeletedEdgeType] = useState<string>('');

  const [connectionLevelEdges, setConnectionLevelEdges] = useState<EdgeType[]>([]);
  const [selectedConnectionLevel, setSelectedConnectionLevel] = useState<string | null>(null);

  const [connectionReasonEdge, setConnectionReasonEdge] = useState<EdgeType | null>(null);
  const [selectedConnectionReason, setSelectedConnectionReason] = useState<string | null>(null);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalText, setModalText] = useState<string>('');

  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState<boolean>(false);
  const [feedbackModalText, setFeedbackModalText] = useState<string>('');

  const [isLodingPost, setIsLoadingPost] = useState<boolean>(false);

  const [commonEdges, setCommonEdges] = useState<string[]>([]);
  const [rating, setRating] = useState<number>(0);

  useEffect(() => {
    fetchEdgeTypes();
  }, []);

  useEffect(() => {
    fetchCommonEdgeTypes(selectedUser);
    setRating(0);
  }, [selectedUser.id]);

  const fetchEdgeTypes = async () => {
    setLoading(true);
    try {
      const response = await apiNetworking.get('/edge/types');

      const edgeTypesResponse: EdgeType[] = response.data;

      const levels = edgeTypesResponse.filter(e => connectionEdgesTypes.includes(e.code));
      setConnectionLevelEdges(levels || []);

      const reason = edgeTypesResponse.find(e => e.code === 'CONNECTION_REASON');
      setConnectionReasonEdge(reason || null);
    } catch (error) {
      console.error('Erro ao buscar tipos de arestas:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommonEdgeTypes = async (selectedUser: MappedMember) => {
    try {
      const edgesResponse = await apiNetworking.get(
        `/edge/find?from_user_uuid=${user?.id}&to_user_uuid=${selectedUser?.id}`
      );
      const edges = edgesResponse.data;

      console.log('edges', edges);

      // Map the edge types for common edges
      setCommonEdges(edges.map((edge: CommonEdge) => edge.type_code));

      // Check for "RATED" edge and set rating
      const ratedEdge = edges.find((edge: CommonEdge) => edge.type_code === 'RATED');
      if (ratedEdge) {
        setRating(Number(ratedEdge.value)); // Assuming setRating is a state setter
      } else {
        setRating(0);
      }

      // Check for "CONNECTION_LEVEL" edges and set rating

      const clEdge = edges.find((edge: CommonEdge) =>
        connectionEdgesTypes.includes(edge.type_code)
      );
      if (clEdge) {
        setSelectedConnectionLevel(clEdge.type_code);
      } else {
        setSelectedConnectionLevel(null);
      }

      // Check for "CONNECTION_REASON" edge and set rating
      const crEdge = edges.find((edge: CommonEdge) => edge.type_code === 'CONNECTION_REASON');
      if (crEdge) {
        setSelectedConnectionReason(crEdge.value); // Assuming setRating is a state setter
      } else {
        setSelectedConnectionReason(null);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      alert(`Erro ao salvar informação. Contate o suporte. Erro: ${err}`);
    }
    // finally {
    // setLoading(false);
    // }
  };

  const handleChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;

    console.log('name', name);
    console.log('value', value);

    switch (name) {
      case 'CONNECTION_LEVEL':
        setSelectedConnectionLevel(value);
        setCommonEdges([...commonEdges, name]);
        postEdge(value);
        break;
      case 'RATED':
        postEdge(name, value);
        setRating(Number(value));
        break;
      case 'CONNECTED':
        setSeletedEdgeType('CONNECTED');
        if (commonEdges.includes('CONNECTED')) {
          toastError('Você já tem uma solicitação de conexão com esta pessoa.');
          break;
        } else {
          setModalText('Deseja realmente se conectar com esta pessoa?');
          setFeedbackModalText('A conexão foi enviada com sucesso!');
          setCommonEdges([...commonEdges, name]);
        }
        setIsModalOpen(true);
        break;
      case 'SCHEDULED':
        if (commonEdges.includes('SCHEDULED')) {
          toastError('Você já tem uma solicitação de agendamento com esta pessoa.');
          break;
        } else {
          setSeletedEdgeType('SCHEDULED');
          setModalText(
            'Deseja realmente registrar que você quer agendar uma reunião com esta pessoa?'
          );
          setFeedbackModalText(
            'Agendamento solicitado. Equipe Rainmakers entrará em contato para data e horário.'
          );
          setIsModalOpen(true);
          setCommonEdges([...commonEdges, name]);
        }
        break;
      case 'WHATSAPP':
        setSeletedEdgeType('WHATSAPP');
        if (commonEdges.includes('WHATSAPP')) {
          toastError('Você já tem uma solicitação de WhatsApp com esta pessoa.');
          break;
        } else {
          setModalText('Você realmente deseja trocar Whastapp com esta pessoa?');
          setFeedbackModalText(
            'A sua solicitação de whatsApp foi realizada. A equipe da RainMakers vai entrar em contato para facilitar o processo. Aguarde contato'
          );
          setIsModalOpen(true);
          setCommonEdges([...commonEdges, name]);
          break;
        }
      default:
        break;
    }
  };

  const handleConfirm = async () => {
    try {
      postEdge(selectedEdgeType);
    } catch {
      alert('Erro ao salvar informação. Contate o suporte.');
    } finally {
      setIsModalOpen(false);
    }
  };

  const postEdge = async (type_code: string, value: string | null = null) => {
    setIsLoadingPost(true);
    try {
      const from_user_uuid = user?.id;
      const to_user_uuid = selectedUser?.id;

      const data = {
        from_user_uuid,
        to_user_uuid,
        type_code,
        value,
      };

      const response = await apiNetworking.post('/edge/create', data);

      console.log('response', response);

      setSelectedConnectionLevel(response.data.type_code);

      toastSuccess('Dados salvos com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar aresta:', error);
      toastError('Erro ao salvar informação. Contate o suporte.');
    } finally {
      setIsLoadingPost(false);
    }
  };

  if (loading) {
    return <Spin />;
  }

  return (
    <>
      <Stack gap={1}>
        <Paper>
          <UserPicture selectedUser={selectedUser} />
          <Stack direction={'column'} gap={1}>
            <FormControl>
              <InputLabel id="demo-simple-select-helper-label">
                Selecione o nível da conexão
              </InputLabel>
              <Select
                value={selectedConnectionLevel || ''}
                label={<Text color="white">Selecione o nível da conexão</Text>}
                name="CONNECTION_LEVEL"
                onChange={handleChange}
              >
                {connectionLevelEdges?.map((v: EdgeType) => (
                  <MenuItem key={v.code} value={v.code}>
                    {v.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* <FormControl>
              <InputLabel id="demo-simple-select-helper-label">
                Selecione o motivo da conexão
              </InputLabel>
              <Select
                value={selectedConnectionReason || ''}
                label="Selecione o motivo da conexão"
                name="CONNECTION_REASON"
                onChange={handleChange}
              >
                {connectionReasonEdge?.extras?.values.map(v => (
                  <MenuItem key={v.value} value={v.value}>
                    {v.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl> */}
          </Stack>
        </Paper>
        <Paper>
          <Stack direction={'column'} gap={1} sx={{ alignItems: 'center' }}>
            <Text size="large">Que nota você daria para este membro?</Text>
            <Rating
              size="large"
              sx={{ scale: '1.5' }}
              value={rating}
              onChange={(event, newValue) =>
                handleChange({
                  target: { name: 'RATED', value: newValue?.toString() || '' },
                } as SelectChangeEvent)
              }
            />
          </Stack>
        </Paper>
        <Paper sx={{ display: 'flex', justifyContent: 'space-around' }}>
          <ButtonEdges
            ariaLabel="connected"
            label="Conectar"
            icon={PersonAddAlt1Icon}
            active={commonEdges.includes('CONNECTED')}
            onClick={() =>
              handleChange({
                target: { name: 'CONNECTED', value: 'CONNECTED' },
              } as SelectChangeEvent)
            }
          />
          <ButtonEdges
            ariaLabel="scheduled"
            label="Agendar"
            icon={CalendarMonthIcon}
            active={commonEdges.includes('SCHEDULED')}
            onClick={() =>
              handleChange({
                target: { name: 'SCHEDULED', value: 'SCHEDULED' },
              } as SelectChangeEvent)
            }
          />
          <ButtonEdges
            ariaLabel="whatsapp"
            label="WhatsApp"
            icon={WhatsAppIcon}
            active={commonEdges.includes('WHATSAPP')}
            onClick={() =>
              handleChange({ target: { name: 'WHATSAPP', value: 'WHATSAPP' } } as SelectChangeEvent)
            }
          />
        </Paper>
      </Stack>

      <GeneralConfirmationModal
        variant="normal"
        isOpen={isModalOpen}
        loading={isLodingPost}
        onClose={() => setIsModalOpen(false)}
        text={modalText}
        onConfirm={handleConfirm}
      />
      <GeneralConfirmationModal
        variant="justClose"
        isOpen={isFeedbackModalOpen}
        loading={false}
        onClose={() => setIsFeedbackModalOpen(false)}
        text={feedbackModalText}
        onConfirm={() => setIsFeedbackModalOpen(false)}
      />
    </>
  );
};

interface ButtonEdgesProps {
  ariaLabel: string;
  icon: typeof SvgIcon; // Icon component type
  label: string;
  active: boolean;
  onClick: () => void; // Callback for click event
}

const ButtonEdges: React.FC<ButtonEdgesProps> = ({
  ariaLabel,
  icon: Icon,
  label,
  active,
  onClick,
}) => {
  return (
    <IconButton
      aria-label={ariaLabel}
      onClick={onClick}
      sx={active ? { color: '#FFD700' } : undefined}
    >
      <Stack direction="column" sx={{ alignItems: 'center' }}>
        <Icon fontSize="large" sx={{ fontSize: 40 }} />
        <Text size="medium">{label}</Text>
      </Stack>
    </IconButton>
  );
};
